import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const SERIES = [
  { key: 'active', label: 'Active', color: '#16a34a' },        // green-600
  { key: 'trialing', label: 'Trialing', color: '#3b82f6' },    // blue-500
  { key: 'pastDue', label: 'Past due', color: '#f59e0b' },     // amber-500
  { key: 'canceled', label: 'Canceled', color: '#9ca3af' },    // gray-400
];

function buildSeries(history, kind) {
  if (!history || history.length === 0) return [];
  // history is descending by date from the API; chart wants ascending.
  return [...history]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((row) => {
      const bucket = row[kind] || {};
      return {
        date: row.date,
        active: bucket.totalActive || 0,
        trialing: bucket.totalTrialing || 0,
        pastDue: bucket.totalPastDue || 0,
        canceled: bucket.totalCanceled || 0,
      };
    });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded shadow px-3 py-2 text-sm">
      <div className="font-medium text-gray-900 mb-1">
        {format(new Date(label), 'MMM d, yyyy')}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center text-gray-700">
          <span
            className="inline-block w-2 h-2 rounded-sm mr-2"
            style={{ backgroundColor: p.color }}
          />
          <span className="mr-2">{p.name}:</span>
          <span className="font-medium">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function SubscriptionTrendChart({ history }) {
  const [kind, setKind] = useState('individual');

  const data = useMemo(() => buildSeries(history, kind), [history, kind]);

  if (!history || history.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Subscription trend</h3>
        </div>
        <div className="flex flex-col items-center justify-center h-[250px] text-gray-500 text-sm">
          <p>No snapshots recorded yet.</p>
          <p className="mt-1 text-xs">Build today&apos;s snapshot using the button above to start the trend line.</p>
        </div>
      </div>
    );
  }

  const tabButton = (value, label) => (
    <button
      type="button"
      onClick={() => setKind(value)}
      className={`px-3 py-1 text-sm rounded ${
        kind === value
          ? 'bg-gray-900 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Subscription trend</h3>
        <div className="flex items-center gap-2">
          {tabButton('individual', 'Individuals')}
          {tabButton('listing', 'Listings')}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(d) => format(new Date(d), 'MMM d')}
          />
          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {SERIES.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stackId="1"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.6}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
